// @ts-check
/* global module, URL */
/**
 * Security response headers for every portal (admin, auth, partner, payments).
 *
 * Plain CommonJS on purpose: each app's `next.config.js` runs untranspiled in
 * Node, so it can only `require()` a `.js` file. Edit the policy here — the
 * four configs only call `securityHeaders(app, process.env)`.
 *
 * The CSP ships as `Content-Security-Policy-Report-Only` (CT-188). Violations
 * are sent to PostHog (CSP Violation Reports dashboard template). Promote to an
 * enforcing policy by flipping `CSP_HEADER` once the reports have been triaged,
 * and bump `CSP_VERSION` whenever the policy changes so reports can be split
 * per revision.
 *
 * `ENFORCED_DIRECTIVES` lists the directives an app already enforces through a
 * second, blocking `Content-Security-Policy` header. Partner enforces
 * `img-src` because Alti chat (full page and the floating widget on every
 * dashboard/onboarding page) renders model-written markdown, and a remote
 * image there is a zero-click beacon (S-FE-1).
 */

const CSP_HEADER = 'Content-Security-Policy-Report-Only'
const ENFORCED_CSP_HEADER = 'Content-Security-Policy'
const CSP_VERSION = 2

/** @type {Partial<Record<PortalApp, string[]>>} */
const ENFORCED_DIRECTIVES = {
  partner: ['img-src']
}

/** Fallback for `NEXT_PUBLIC_ALLOWED_REDIRECT_DOMAINS` (see `./index.ts`). */
const DEFAULT_ALLOWED_REDIRECT_DOMAINS = [
  'alternativepayments.io',
  'altpay.network'
]

const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com'

/** posthog-js lazy-loads recorder/heatmap bundles and rotates ingestion subdomains. */
const POSTHOG = ['https://*.posthog.com']

const LOGO_HOSTS = [
  'https://logos.staging.alternativepayments.io',
  'https://logos.demo.alternativepayments.io',
  'https://logos.alternativepayments.io'
]

/**
 * Email template previews (`EmailTemplatePreview` srcDoc iframes inherit this
 * CSP) hardcode the prod logo/powered-by images. Path-scoped to our own static
 * assets so non-prod origins can render them without reopening remote images.
 */
const EMAIL_ASSETS = ['https://app.alternativepayments.io/email/']

/**
 * `@evervault/react` loads js.evervault.com, frames inputs/ui-components and
 * calls api/keys plus the `<id>.relay.evervault.com` card-reveal relay.
 */
const EVERVAULT = {
  'script-src': ['https://js.evervault.com'],
  'connect-src': ['https://*.evervault.com'],
  'frame-src': ['https://*.evervault.com']
}

/** Messenger widget hosts from Zendesk's CSP guide (`lib/utils/Zendesk`). */
const ZENDESK = {
  'script-src': ['https://*.zdassets.com'],
  'connect-src': [
    'https://*.zdassets.com',
    'https://*.zendesk.com',
    'wss://*.zendesk.com',
    'https://*.smooch.io',
    'wss://api.smooch.io'
  ],
  'img-src': [
    'https://*.zdassets.com',
    'https://*.zendesk.com',
    'https://media.smooch.io',
    'https://*.zdusercontent.com'
  ],
  'frame-src': ['https://*.zdassets.com', 'https://*.zendesk.com'],
  'media-src': ['https://static.zdassets.com']
}

/**
 * @typedef {'admin' | 'auth' | 'partner' | 'payments'} PortalApp
 * @typedef {Record<string, string | undefined>} Env
 * @typedef {Record<string, string[]>} Directives
 */

/**
 * Origin of an absolute URL taken from the environment; `null` when unset or
 * not a URL, so a missing variable drops the source instead of emitting junk.
 *
 * @param {string | undefined} url
 * @returns {string | null}
 */
function originOf(url) {
  if (!url) return null
  try {
    return new URL(url).origin
  } catch {
    return null
  }
}

/**
 * @param {Array<string | null | undefined>} values
 * @returns {string[]}
 */
function compact(values) {
  return values.filter(
    /** @returns {value is string} */ value => Boolean(value)
  )
}

/**
 * Hosts auth may send the browser to after an Ory form POST. Chromium applies
 * `form-action` to the redirect chain, so the post-login `return_to` targets
 * (the same allowlist `validateRedirectUrl` enforces) must be listed.
 *
 * @param {Env} env
 * @returns {string[]}
 */
function redirectDomainSources(env) {
  const domains = env.NEXT_PUBLIC_ALLOWED_REDIRECT_DOMAINS
    ? env.NEXT_PUBLIC_ALLOWED_REDIRECT_DOMAINS.split(',').map(d =>
        d.trim().toLowerCase()
      )
    : DEFAULT_ALLOWED_REDIRECT_DOMAINS
  return domains.flatMap(domain =>
    domain === 'localhost'
      ? ['http://localhost:*']
      : [`https://${domain}`, `https://*.${domain}`]
  )
}

/**
 * @param {string | undefined} kratosUrl
 * @returns {string | null}
 */
function oryAssetsSource(kratosUrl) {
  if (!kratosUrl || !originOf(kratosUrl)) return null
  return new URL('.well-known/ory/', kratosUrl.replace(/\/?$/, '/')).href
}

/**
 * Sources each app needs on top of the shared baseline.
 *
 * @param {PortalApp} app
 * @param {Env} env
 * @returns {Directives[]}
 */
function appDirectives(app, env) {
  const graph = {
    'connect-src': compact([originOf(env.NEXT_PUBLIC_GRAPH_URI)])
  }

  switch (app) {
    case 'admin':
      return [
        graph,
        { 'connect-src': compact([originOf(env.NEXT_PUBLIC_AI_INSIGHTS_URL)]) }
      ]
    case 'auth': {
      const kratos = originOf(env.NEXT_PUBLIC_KRATOS_URL)
      return [
        graph,
        ZENDESK,
        {
          // Ory UI script nodes load `<kratos>/.well-known/ory/webauthn.js`.
          // Path-scoped: the Kratos host also serves the GraphQL gateway.
          'script-src': compact([oryAssetsSource(env.NEXT_PUBLIC_KRATOS_URL)]),
          'connect-src': compact([kratos]),
          'form-action': compact([
            kratos,
            originOf(env.NEXT_PUBLIC_HYDRA_URL),
            ...redirectDomainSources(env)
          ])
        }
      ]
    }
    case 'partner':
      return [
        graph,
        EVERVAULT,
        ZENDESK,
        {
          'img-src': EMAIL_ASSETS,
          'connect-src': compact([
            originOf(env.NEXT_PUBLIC_PARTNER_ASSISTANT_URI)
          ])
        }
      ]
    case 'payments':
      return [graph, EVERVAULT, ZENDESK]
  }
}

/**
 * @param {PortalApp} app
 * @param {Env} env
 * @returns {Directives}
 */
function cspDirectives(app, env) {
  const isDev = env.NODE_ENV === 'development'
  // Deployed builds point posthog-js at the first-party reverse proxy
  // (posthog.alternativepayments.io), which serves both API and lazy bundles.
  const posthog = compact([...POSTHOG, originOf(env.NEXT_PUBLIC_POSTHOG_HOST)])
  // Vercel injects its feedback toolbar (script + iframe) into previews only.
  const vercelToolbar =
    env.NEXT_PUBLIC_VERCEL_ENV === 'preview' ? ['https://vercel.live'] : []

  /** @type {Directives} */
  const policy = {
    'default-src': ["'self'"],
    // Pages Router emits no inline executable scripts (`__NEXT_DATA__` is
    // JSON), so no nonce is needed. React Refresh needs eval in `next dev` only.
    'script-src': [
      "'self'",
      ...(isDev ? ["'unsafe-eval'"] : []),
      ...posthog,
      ...vercelToolbar
    ],
    // Emotion and React `style` props inject inline styles at runtime.
    'style-src': ["'self'", "'unsafe-inline'", 'https://fonts.googleapis.com'],
    'img-src': ["'self'", 'data:', 'blob:', ...LOGO_HOSTS],
    'font-src': ["'self'", 'data:', 'https://fonts.gstatic.com'],
    'connect-src': ["'self'", ...posthog],
    'frame-src': ["'self'", ...vercelToolbar],
    'media-src': ["'self'"],
    // PDF.js (partner bill preview) and posthog-js recorder spawn workers.
    'worker-src': ["'self'", 'blob:', 'data:'],
    'object-src': ["'none'"],
    'base-uri': ["'self'"],
    'form-action': ["'self'"],
    // Same intent as `X-Frame-Options: DENY`.
    'frame-ancestors': ["'none'"]
  }

  for (const extra of appDirectives(app, env)) {
    for (const [directive, sources] of Object.entries(extra)) {
      policy[directive] = [
        ...new Set([...(policy[directive] ?? []), ...sources])
      ]
    }
  }

  return policy
}

/**
 * @param {Directives} policy
 * @param {Env} env
 * @returns {string}
 */
function serializePolicy(policy, env) {
  const directives = Object.entries(policy).map(
    ([directive, sources]) => `${directive} ${sources.join(' ')}`
  )

  const reportUrl = cspReportUrl(env)
  if (reportUrl) directives.push(`report-uri ${reportUrl}`, 'report-to posthog')

  return directives.join('; ')
}

/**
 * The enforcing policy: only the app's `ENFORCED_DIRECTIVES`, with the same
 * sources as the report-only policy. `null` when the app enforces nothing.
 *
 * @param {PortalApp} app
 * @param {Directives} policy
 * @param {Env} env
 * @returns {string | null}
 */
function enforcedPolicy(app, policy, env) {
  const enforced = ENFORCED_DIRECTIVES[app] ?? []
  if (enforced.length === 0) return null
  /** @type {Directives} */
  const subset = {}
  for (const directive of enforced) subset[directive] = policy[directive]
  return serializePolicy(subset, env)
}

/**
 * PostHog CSP ingestion endpoint (https://posthog.com/docs/csp-tracking).
 * Production builds only, so `next dev` never reports into the real project.
 *
 * @param {Env} env
 * @returns {string | null}
 */
function cspReportUrl(env) {
  const token = env.NEXT_PUBLIC_POSTHOG_KEY
  if (env.NODE_ENV !== 'production' || !token) return null
  const host = originOf(env.NEXT_PUBLIC_POSTHOG_HOST) ?? DEFAULT_POSTHOG_HOST
  return `${host}/report/?token=${encodeURIComponent(token)}&v=${CSP_VERSION}`
}

/**
 * Deny browser features the portals never use. Partner keeps geolocation
 * (PAD consent and CA bank-account forms call `getUserLocation` in
 * `utils/browser`) and autoplay (Alti onboarding video); payments keeps
 * geolocation for the same PAD flows.
 *
 * @param {PortalApp} app
 * @returns {string}
 */
function permissionsPolicy(app) {
  const usesGeolocation = app === 'partner' || app === 'payments'
  const usesAutoplay = app === 'partner'
  return [
    'accelerometer=()',
    `autoplay=${usesAutoplay ? '(self)' : '()'}`,
    'camera=()',
    'display-capture=()',
    'encrypted-media=()',
    'fullscreen=(self)',
    `geolocation=${usesGeolocation ? '(self)' : '()'}`,
    'gyroscope=()',
    'magnetometer=()',
    'microphone=()',
    'midi=()',
    'payment=()',
    'picture-in-picture=()',
    'usb=()',
    'xr-spatial-tracking=()'
  ].join(', ')
}

/**
 * Headers for the catch-all `source: '/(.*)'` rule in `next.config.js`.
 *
 * @param {PortalApp} app
 * @param {Env} env build-time `process.env`
 * @returns {Array<{ key: string, value: string }>}
 */
function securityHeaders(app, env) {
  const reportUrl = cspReportUrl(env)
  const policy = cspDirectives(app, env)
  const enforced = enforcedPolicy(app, policy, env)
  return [
    { key: 'Referrer-Policy', value: 'no-referrer' },
    {
      key: 'Strict-Transport-Security',
      value: 'max-age=63072000; includeSubDomains; preload'
    },
    { key: 'X-Content-Type-Options', value: 'nosniff' },
    { key: 'X-Frame-Options', value: 'DENY' },
    { key: 'Permissions-Policy', value: permissionsPolicy(app) },
    { key: CSP_HEADER, value: serializePolicy(policy, env) },
    ...(enforced ? [{ key: ENFORCED_CSP_HEADER, value: enforced }] : []),
    ...(reportUrl
      ? [{ key: 'Reporting-Endpoints', value: `posthog="${reportUrl}"` }]
      : [])
  ]
}

module.exports = { DEFAULT_ALLOWED_REDIRECT_DOMAINS, securityHeaders }
